import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, QuestionMarkCircleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import useDocumentMetadata from '../hooks/useDocumentMetadata';

const Faq = () => {
  useDocumentMetadata(
    'Frequently Asked Questions (FAQ) - Dakhla',
    'Find answers to common questions about university admissions, eligibility criteria, application process, and fee structures on Dakhla.'
  );

  const [openFaqIndex, setOpenFaqIndex] = useState(0); // First one open by default

  const faqs = [
    {
      question: "How do I apply to universities through Dakhla?",
      answer: "Simply browse universities, click 'DirectApply' on your preferred university, fill out the application form, and upload required documents. Your application will be sent directly to the university."
    },
    {
      question: "What is the Dakhla Exam?",
      answer: "The Dakhla Exam is a standardized test that helps universities assess student readiness. It covers various subjects and helps match students with suitable programs."
    },
    {
      question: "Is there a fee for using Dakhla?",
      answer: "Dakhla is free for students to browse and apply to universities. Some universities may have their own application fees."
    },
    {
      question: "How do I track my application status?",
      answer: "Once you create an account and apply, you can track all your applications in your student dashboard."
    },
    {
      question: "Can universities update their information?",
      answer: "Yes, universities have their own accounts where they can update programs, fees, deadlines, and manage applications."
    },
    {
      question: "What documents do I need for applications?",
      answer: "Typically you'll need: Intermediate certificates, transcripts, CNIC copy, passport photos, and any specific documents required by the university."
    },
    {
      question: "How does the recommendation system work?",
      answer: "Our AI analyzes your academic background, interests, and preferences to suggest universities that best match your profile."
    },
    {
      question: "Can I apply to multiple universities?",
      answer: "Yes! You can apply to as many universities as you want through a single Dakhla account."
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Header */}
      <div className="relative bg-[#1E256F] text-white py-20 overflow-hidden shadow-sm">
        <div
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          style={{
            backgroundImage: "linear-gradient(to right, rgba(255, 255, 255, 0.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, 0.07) 1px, transparent 1px)",
            backgroundSize: "24px 24px"
          }}
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-tight mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-slate-200 text-base sm:text-lg max-w-2xl mx-auto font-normal">
            Find quick answers to common questions about Dakhla, admissions, programs, and application processes.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openFaqIndex === index;
            return (
              <div
                key={index}
                className="card bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden transition-all duration-200 hover:border-slate-300"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full py-5 px-6 text-left font-bold text-slate-800 text-base flex justify-between items-center gap-4 hover:text-primary-600 transition-colors focus:outline-none"
                >
                  <span>{faq.question}</span>
                  <ChevronDownIcon
                    className={`h-5 w-5 text-slate-400 shrink-0 transition-transform duration-300 ${
                      isOpen ? 'rotate-180 text-primary-600' : ''
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="px-6 pb-5 pt-1 text-slate-600 text-sm leading-relaxed border-t border-slate-100">
                    {faq.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Still Have Questions Banner */}
        <div className="mt-12 bg-gradient-to-r from-primary-600 to-indigo-600 rounded-2xl p-6 sm:p-8 text-white shadow-lg flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl font-bold mb-1">Still have questions?</h3>
            <p className="text-slate-100 text-sm">Can't find the answer you're looking for? Please reach out to our team.</p>
          </div>
          <Link
            to="/contact"
            className="shrink-0 inline-flex items-center gap-2 px-5 py-3 bg-white text-primary-700 hover:bg-slate-50 text-sm font-bold rounded-xl shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all"
          >
            <EnvelopeIcon className="h-4 w-4" />
            <span>Contact Us</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Faq;
